import { useEffect, useRef } from 'preact/compat';
import { useModal } from './hooks/useModal';
import { Layer } from '../UNSAFE_Layer';
import ModalBackdrop from './ModalBackdrop';
import ModalManager from './ModalManager';
import { baseStyle } from './themes/ModalStyles.css';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

// Global ModalManager singleton instance
const globalModalManager = new ModalManager();

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;
type UseModalProps = Parameters<typeof useModal>[0];
type Props = UseModalProps &
  IntrinsicProps & {
    /**
     * Determines if the modal is active.
     */
    isOpen: boolean;
    /**
     * Specifies if modal backdrop is visible or transparent. Default is 'scrim' (visible).
     */
    backdropVariant?: 'scrim' | 'transparent';
    /**
     * A callback to be invoked when a click on the modal backdrop occurs.
     */
    onBackdropClick?: () => void;
  };

/**
 * Modal is a low-level component that provides the 'modality' feature with built-in overlay (scrim).
 * It is typically used for building higher-level components (such as 'Dialog' or 'Popup') that need
 * to support modal behavior preventing interaction with the rest of the page while the modal is open.
 */
const Modal = ({ children, isOpen, onBackdropClick, backdropVariant }: Props) => {
  // get the global ModalContext instance
  const modalManager = globalModalManager;

  const { backdropRef } = useModal({ isOpen, onBackdropClick });
  // main modal element ref
  const modalRef = useRef<HTMLDivElement>(null);

  // modal ref cache, required to lookup the item in modal manger when the actual ref is unmounted
  const modal = useRef<{
    modalRef?: typeof modalRef.current;
  }>({});

  // populates and returns the modal ref cache
  const getModal = () => {
    modal.current.modalRef = modalRef.current;
    return modal.current;
  };

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalManager?.push(getModal());
    } else {
      if (modal.current.modalRef) {
        modalManager?.pop(getModal());
      }
    }
  }, [isOpen, modalRef.current]);

  useEffect(() => {
    return () => {
      if (modal.current.modalRef) {
        modalManager?.pop(getModal());
      }
    };
  }, []);

  return isOpen ? (
    <Layer>
      <div ref={modalRef} class={baseStyle}>
        <ModalBackdrop ref={backdropRef} variant={backdropVariant} />
        {children}
      </div>
    </Layer>
  ) : null;
};

export default Modal;
